import {
  CaseType,
  ExhibitStyle,
  PunctuationType,
  TokenType
} from "../types/exhibit_formats";
import { NonEmptyVisa, VISAVALUE } from "../types/visa.type";

/**
 * Gets the display label for a document based on its type and customType.
 * If the document type is Other/Custom and has a customType, returns "Other (customType)".
 * Otherwise, returns just the base type.
 */

const getShortString = (arg: string | undefined, limit = 20) => {
  if (arg) {
    if (arg.length > 20) return arg.substring(0, limit).concat("...");
    return arg;
  }
  return "";
};

// Helper Functions
export const getNextDocumentName = (
  documents: { templateName?: string }[]
): string => {
  // filter only "Untitled template..."
  const names = documents
    .map((doc) => doc.templateName)
    .filter((name) => name?.startsWith("Untitled template"));

  // extract the number from each name, default to 0 if we cannot parse
  const maxIndex = names
    .map((name) => {
      const match = name?.match(/^Untitled template\s+(\d+)/);
      if (!match) {
        return 0;
      }
      return parseInt(match[1], 10) || 0;
    })
    .reduce((max, num) => Math.max(max, num), 0);

  // increment by 1
  return `Untitled template ${maxIndex + 1}`;
};

type NameOptions = {
  baseName: string;
  existingNames: string[];
  separator?: string; // e.g., " ", "-", "_", " (", etc.
  suffixRegex?: RegExp; // optional override for advanced suffix parsing
};

export const getNextAvailableName = ({
  baseName,
  existingNames,
  separator = " ",
  suffixRegex
}: NameOptions): string => {
  const escapedBase = baseName.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const defaultSuffixRegex = new RegExp(
    `^${escapedBase}(?:${separator}(\\d+))?$`
  );
  const pattern = suffixRegex ?? defaultSuffixRegex;

  let maxIndex = -1;
  let baseUsed = false;

  for (const name of existingNames) {
    const match = name.match(pattern);
    if (match) {
      if (match[1] !== undefined) {
        const num = parseInt(match[1], 10);
        if (!Number.isNaN(num)) {
          maxIndex = Math.max(maxIndex, num);
        }
      } else {
        baseUsed = true;
        maxIndex = Math.max(maxIndex, 0);
      }
    }
  }

  if (!baseUsed && maxIndex <= 0) {
    return baseName;
  }

  return `${baseName}${separator}${maxIndex + 1}`;
};

const VISA_VALUES = new Set<string>(Object.values(VISAVALUE));

const isValidVisaKey = (visa: unknown): visa is NonEmptyVisa =>
  typeof visa === "string" && visa !== VISAVALUE.EMPTY && VISA_VALUES.has(visa);

const toSnakeCase = (str: string): string => {
  return str
    .toLowerCase()
    .replace(/\s+/g, "_")
    .replace(/[^\w_]/g, "");
};

export const toTitleCase = (str: string): string =>
  str.toLowerCase().replace(/\b\w/g, (char: string) => char.toUpperCase());

export type SortKey = "templateName" | "visa" | "uploadedByName" | "defaultKey";

const removeWhitespace = (str: string) => {
  return str.replace(/\s+/g, "");
};

const getNumberingFormats = (style: ExhibitStyle): string[] => {
  if (style.token === "NONE") return [];

  let base: string[];

  switch (style.token) {
    case "NUMERIC":
      base = ["1", "2", "3"];
      break;
    case "LETTER":
      base = style.case === "UPPER" ? ["A", "B", "C"] : ["a", "b", "c"];
      break;
    case "ROMAN_NUMERAL":
      base = style.case === "UPPER" ? ["I", "II", "III"] : ["i", "ii", "iii"];
      break;
    default:
      base = [""];
  }

  return base.map((v) => `${v}${style.punctuation}`);
};

// Helper function to convert number to roman numeral
export const toRomanNumeral = (num: number): string => {
  const romanNumerals: [number, string][] = [
    [1000, "M"],
    [900, "CM"],
    [500, "D"],
    [400, "CD"],
    [100, "C"],
    [90, "XC"],
    [50, "L"],
    [40, "XL"],
    [10, "X"],
    [9, "IX"],
    [5, "V"],
    [4, "IV"],
    [1, "I"]
  ];

  let result = "";
  let remaining = num;

  for (const [value, symbol] of romanNumerals) {
    while (remaining >= value) {
      result += symbol;
      remaining -= value;
    }
  }

  return result;
};

// Helper function to convert number to letter sequence (1 = A, 27 = AA, etc.)
export const toLetterSequence = (num: number): string => {
  let result = "";
  let remaining = num;

  while (remaining > 0) {
    remaining -= 1;
    result = String.fromCharCode(65 + (remaining % 26)) + result;
    remaining = Math.floor(remaining / 26);
  }

  return result;
};

// Password validation helpers
export interface PasswordValidationResult {
  isValid: boolean;
  requirements: {
    hasMinLength: boolean;
    hasLowercase: boolean;
    hasUppercase: boolean;
    hasNumber: boolean;
    hasSpecial: boolean;
  };
}

export const validatePassword = (
  password: string
): PasswordValidationResult => {
  const hasMinLength = password.length >= 8;
  const hasLowercase = /[a-z]/.test(password);
  const hasUppercase = /[A-Z]/.test(password);
  const hasNumber = /\d/.test(password);
  const hasSpecial = /[!@#$%^&*(),.?":{}|<>]/.test(password);

  const isValid =
    hasMinLength && hasLowercase && hasUppercase && hasNumber && hasSpecial;

  return {
    isValid,
    requirements: {
      hasMinLength,
      hasLowercase,
      hasUppercase,
      hasNumber,
      hasSpecial
    }
  };
};

export const isPasswordValid = (password: string): boolean => {
  return validatePassword(password).isValid;
};

export const extractFileNameFromUrl = (
  url: string,
  fallback = "document.pdf"
): string => {
  const ensurePdf = (name: string) =>
    name.toLowerCase().endsWith(".pdf") ? name : `${name}.pdf`;
  if (!url || typeof url !== "string") return ensurePdf(fallback);
  try {
    const urlObj = new URL(url);
    const pathParts = urlObj.pathname
      .split("/")
      .filter((part) => part.length > 0);
    const candidate =
      pathParts[pathParts.length - 1] || urlObj.hostname || fallback;
    return ensurePdf(candidate);
  } catch {
    const fromSplit = url.split("/").pop();
    const base = fromSplit && fromSplit.length > 0 ? fromSplit : fallback;
    return ensurePdf(base);
  }
};

export const sanitizeNonEmptyStrings = (values: unknown[]): string[] => {
  return values
    .map((v) => (typeof v === "string" ? v.trim() : ""))
    .filter((v): v is string => v.length > 0);
};

const getDocumentLabel = (document: {
  type?: string | null;
  customType?: string | null;
}): string => {
  const baseTypeLabel = document?.type ?? "";
  const isCustomType =
    document?.type === "Other" || document?.type === "Custom";
  const customTypeLabel = isCustomType
    ? document?.customType?.trim()
    : undefined;
  const fullTypeLabel = customTypeLabel
    ? `${baseTypeLabel} (${customTypeLabel})`
    : baseTypeLabel;
  return fullTypeLabel;
};

// export const formatExhibitNumber = (
//   index: number,
//   style: ExhibitStyle
// ): string => {
//   if (!style || style.token === "NONE") return "";

//   let formattedNumber: string;

//   switch (style.token) {
//     case "NUMERIC":
//       formattedNumber = `${index}`;
//       break;
//     case "LETTER": {
//       formattedNumber = toLetterSequence(index);
//       if (style.case === "LOWER") {
//         formattedNumber = formattedNumber.toLowerCase();
//       }
//       break;
//     }
//     case "ROMAN_NUMERAL": {
//       formattedNumber = toRomanNumeral(index);
//       if (style.case === "LOWER") {
//         formattedNumber = formattedNumber.toLowerCase();
//       }
//       break;
//     }
//     default:
//       formattedNumber = `${index}`;
//   }

//   // Add prefix if exists
//   if (style.prefix) {
//     formattedNumber = `${style.prefix}${formattedNumber}`;
//   }

//   // Add punctuation
//   return `${formattedNumber}${style.punctuation}`;
// };

export {
  getNumberingFormats,
  toSnakeCase,
  getShortString,
  isValidVisaKey,
  removeWhitespace,
  getDocumentLabel
};
